<script setup>
import { ref, onMounted, computed } from "vue";
import { useModals } from "@/utils/useModals";
import { useFieldsStore } from "@/stores/storeFieldData";
import { useRequestsStore } from "@/stores/storeRequests";
import { useSettingsStore } from "@/stores/storeSettings";
import { policyUrl, rulesUrl, agreementUrl } from "@/utils/provider";
import { useRoute, RouterLink, useRouter } from "vue-router";
import cookies from "browser-cookies";
import BaseInput from "@/components/ui/BaseInput.vue";
import BasePhoneInput from "@/components/ui/BasePhoneInput.vue";
import BaseCheckbox from "@/components/ui/BaseCheckbox.vue";
import BaseSelect from "@/components/ui/BaseSelect.vue";
import BaseTextarea from "@/components/ui/BaseTextarea.vue";
import BaseFile from "@/components/ui/BaseFile.vue";
import BasePassword from "@/components/ui/BasePassword.vue";
import BasePasswordRepeat from "@/components/ui/BasePasswordRepeat.vue";

const storeField = useFieldsStore();
const storeSettings = useSettingsStore();
const storeRequests = useRequestsStore();
const route = useRoute();

const selectOptions = ref([]);
const password = defineModel();

const { activeModal, modalId, modalObj, closeModal, openModal } = useModals();

const emit = defineEmits(['ageCheck'])
const props = defineProps({
	ageVerified: {
		type: Boolean,
		default: false
	}
})

onMounted(async () => {
	selectOptions.value = await storeRequests.topics();

	if (storeRequests.isAuthenticated) {
		storeField.updateFields("firstName", "disabledBtn", true);
		storeField.updateFields("lastName", "disabledBtn", true);
		storeField.updateFields("phone", "disabledBtn", true);
		storeField.updateFields("email", "disabledBtn", true);
	}
});
</script>
<template>
	<div style="position: absolute; bottom: 100%; overflow: hidden">
		<div id="apm-widget"></div>
	</div>
	<div :class="['modal', { 'is-open': activeModal }]" @click.self="() => (!props.ageVerified ? null : closeModal())">
		<Transition>
			<div v-if="activeModal" :class="['modal__container', {'--small': modalId === 'registrationEnd'}]">
				
				<button class="modal-close" v-if="modalId !== 'ageVerification'" @click="closeModal">
					<svg width="16" height="16"><use href="#x"></use></svg>
				</button>
				<!-- Обратная связь -->
				<div class="modal-content" v-if="modalId === 'feedback'">
					<h5 class="modal-title">Обратная связь</h5>
					<form class="form" @submit.prevent="storeRequests.feedback()">
						<div class="form__block-inputns">
							<div class="form__wrap">
								<BaseInput type="text" placeholder="Имя" :required="true" state-key="firstName" v-model="storeRequests.userData.first_name"> Имя </BaseInput>
							</div>
							<div class="form__wrap">
								<BaseInput type="email" placeholder="Email" state-key="email" v-model="storeRequests.userData.email"> email </BaseInput>
							</div>
							<div class="form__wrap">
								<BaseSelect :select-options></BaseSelect>
							</div>
							<div class="form__wrap">
								<BaseTextarea :max-length="400" state-key="message" placeholder="Сообщение">Сообщение</BaseTextarea>

								<BaseFile />
							</div>
						</div>
						<div class="form__footer">
							<BaseCheckbox :required="true" state-key="agreement">Согласен(на) с обработкой <a :href="policyUrl" target="_blank" rel="noopener noreferrer">персональных данных</a></BaseCheckbox>
							<button :disabled="storeField.isFeedbackBtn" :class="['send-button form__btn btn', { 'not-hover': storeRequests.loaderBtn }]">
								<span v-if="!storeRequests.loaderBtn">Отправить</span>
								<img v-else class="form-loader" width="36" height="36" src="@/assets/img/icons/loader.svg" alt="изображение" />
							</button>
							<p class="form__text-error" v-if="storeRequests.responseError">{{ storeRequests.responseError }}</p>
						</div>
					</form>
				</div>

				<!-- Авторизация -->
				<div class="modal-content" v-if="modalId === 'authorization'">
					<h5 class="modal-title">Авторизация</h5>
					<form class="form form-grid" @submit.prevent="storeRequests.authorization()">
						<div class="form__block-inputns">
							<div class="form__wrap">
								<BaseInput type="email" placeholder="example@mail.ru" state-key="email">Почта</BaseInput>
							</div>
							<div class="form__wrap">
								<BasePassword placeholder="Не менее 8 символов" :required="true" state-key="password"> Пароль </BasePassword>
							</div>
						</div>
						<div class="form__text-btn-inner">
							<button @click="openModal('recoveryPassword')" class="form__text-btn" type="button">Забыли пароль?</button>
						</div>
						<div class="form__footer">
							<button :disabled="storeField.isAuthorizationBtn" :class="['send-button form__btn btn --middle', { 'not-hover': storeRequests.loaderBtn }]">
								<span v-if="!storeRequests.loaderBtn">Войти</span>
								<img v-else class="form-loader" width="36" height="36" src="@/assets/img/icons/loader_white.svg" alt="изображение" />
							</button>
							<p class="form__text-error" v-if="storeRequests.responseError">{{ storeRequests.responseError }}</p>
							<div class="form__btn-wrapper" v-if="storeSettings.statusSurveyRegistration">
								<span>
									Нет аккаунта?
									<button @click="openModal('registration')" class="">Регистрация</button>
								</span>
							</div>
						</div>
					</form>
				</div>

				<!-- Требуется авторизация -->
				<div class="modal-content" v-if="modalId === 'authorizationRequired'">
					<h5 class="modal-title">Требуется авторизация</h5>
					<form class="form form-grid">
						<div class="form__footer">
							<div class="form__btn-wrapper">
								<span>У меня уже есть аккаунт</span>
								<button @click="openModal('authorization')" class="send-button btn form__btn _btn-primary" type="button">Войти</button>
							</div>
							<div class="form__btn-wrapper" v-if="!storeSettings.statusStockPast">
								<span>У меня нет аккаунта</span>
								<RouterLink to="/registration" @click="closeModal" class="btn --full">Регистрация</RouterLink>
								<!-- <button @click="openModal('registration')" class="send-button btn form__btn _btn-primary" type="button">Регистрация</button> -->
							</div>
						</div>
					</form>
				</div>

				<!-- Регистрация -->
				<div class="modal-content" v-if="modalId === 'registration'">
					<h5 class="modal-title">Регистрация</h5>
					<form class="form form-grid" @submit.prevent="storeRequests.registration()">
						<div class="form__block-inputns">
							<div class="form__wrap">
								<BaseInput type="text" placeholder="Имя" :required="true" state-key="firstName">ФИО</BaseInput>
							</div>
							<div class="form__wrap">
								<BaseInput type="email" placeholder="example@mail.ru" state-key="email">Почта</BaseInput>
							</div>
							<div class="form__wrap">
								<BasePhoneInput :required="true" state-key="phone" placeholder="+7 (ХХХ) ХХХ-ХХ-ХХ">Номер телефона </BasePhoneInput>
							</div>
							<div class="form__wrap">
								<BasePassword placeholder="Не менее 8 символов" :required="true" state-key="password" v-model="password">Пароль </BasePassword>
							</div>
							<div class="form__wrap">
								<BasePasswordRepeat placeholder="Повторите пароль" :required="true" state-key="passwordRepeat" :original-password="computed(() => password)">Введите пароль еще раз</BasePasswordRepeat>
							</div>
						</div>
						<div class="form__checkboxes">
							<BaseCheckbox :required="true" state-key="rules"> Я согласен(на) с <a :href="rulesUrl" target="_blank" rel="noopener noreferrer">Правилами </a> проведения конкурса </BaseCheckbox>
							<BaseCheckbox :required="true" state-key="agreement">
								Я даю согласие на обработку моих персональных данных в соответствии с
								<a :href="agreementUrl" target="_blank" rel="noopener noreferrer">Политикой конфиденциальности</a>
							</BaseCheckbox>
						</div>
						<div class="form__footer">
							<button :disabled="storeField.isRegestrationBtn" :class="['send-button form__btn btn --middle', { 'not-hover': storeRequests.loaderBtn }]">
								<span v-if="!storeRequests.loaderBtn">Регистрация</span>
								<img v-else class="form-loader" width="36" height="36" src="@/assets/img/icons/loader_white.svg" alt="изображение" />
							</button>
							<p class="form__text-error" v-if="storeRequests.responseError">{{ storeRequests.responseError }}</p>
							<div class="form__btn-wrapper">
								<span>
									У меня уже есть аккаунт.
									<button @click="openModal('authorization')" class="">Войти</button>
								</span>
							</div>
						</div>
					</form>
				</div>
			
				<!-- Восстановление пароля -->
				<div class="modal-content" v-if="modalId === 'recoveryPassword'">
					<h5 class="modal-title">Восстановление пароля</h5>
					<form class="form form-grid" @submit.prevent="storeRequests.recoveryPassword()">
						<div class="form__block-inputns">
							<div class="form__wrap">
								<BaseInput type="email" placeholder="example@mail.ru" state-key="email">Почта</BaseInput>
							</div>
						</div>
						<div class="form__footer">
							<button :disabled="storeField.isRecoveryPasswordBtn" :class="['send-button form__btn btn --middle', { 'not-hover': storeRequests.loaderBtn }]">
								<span v-if="!storeRequests.loaderBtn">Восстановить</span>
								<img v-else class="form-loader" width="36" height="36" src="@/assets/img/icons/loader_white.svg" alt="изображение" />
							</button>
							<p class="form__text-error" v-if="storeRequests.responseError">{{ storeRequests.responseError }}</p>
						</div>
					</form>
				</div>

				<!-- Изменение пароля -->
				<div class="modal-content" v-if="modalId === 'changeUserPassword'">
					<h5 class="modal-title">Изменение пароля</h5>
					<form class="form form-grid" @submit.prevent="storeRequests.changeUserPassword()">
						<div class="form__block-inputns">
							<div class="form__wrap">
								<BasePassword placeholder="Не менее 8 символов" :required="true" state-key="password" v-model="password"> Новый пароль </BasePassword>
							</div>
							<div class="form__wrap">
								<BasePasswordRepeat placeholder="Повторите пароль" :required="true" state-key="passwordRepeat" :original-password="computed(() => password)"> Повторите новый пароль </BasePasswordRepeat>
							</div>
						</div>
						<div class="form__footer">
							<p class="form__text-error" v-if="storeRequests.responseError">{{ storeRequests.responseError }}</p>
							<button :disabled="storeField.isCahngePasswordBtn" :class="['send-button form__btn btn --middle', { 'not-hover': storeRequests.loaderBtn }]">
								<span v-if="!storeRequests.loaderBtn">Изменить</span>
								<img v-else class="form-loader" width="36" height="36" src="@/assets/img/icons/loader_white.svg" alt="изображение" />
							</button>
						</div>
					</form>
				</div>

				<!-- New password -->
				<div class="modal-content" v-if="modalId === 'newUserPassword'">
					<h5 class="modal-title">Придумайте новый пароль</h5>
					<form class="form form-grid" @submit.prevent="storeRequests.newUserPassword(modalObj.token, modalObj.uuid)">
						<div class="form__block-inputns">
							<div class="form__wrap">
								<BasePassword placeholder="Не менее 8 символов" :required="true" state-key="password" v-model="password">Пароль</BasePassword>
							</div>
							<div class="form__wrap">
								<BasePasswordRepeat placeholder="Повторите пароль" :required="true" state-key="passwordRepeat" :original-password="computed(() => password)">Введите пароль еще раз</BasePasswordRepeat>
							</div>
						</div>
						<div class="form__footer">
							<p class="form__text-error" v-if="storeRequests.responseError">{{ storeRequests.responseError }}</p>
							<button :disabled="storeField.isCahngePasswordBtn" :class="['send-button form__btn btn --middle', { 'not-hover': storeRequests.loaderBtn }]">
								<span v-if="!storeRequests.loaderBtn">Сохранить и войти</span>
								<img v-else class="form-loader" width="36" height="36" src="@/assets/img/icons/loader_white.svg" alt="изображение" />
							</button>
						</div>
					</form>
				</div>

				<!-- Изменение данных -->
				<div class="modal-content" v-if="modalId === 'changeUserProfile'">
					<h5 class="modal-title">Изменение данных</h5>
					<form class="form form-grid" @submit.prevent="storeRequests.changeUserProfile()">
						<div class="form__block-inputns">
							<div class="form__wrap">
								<BaseInput type="text" placeholder="Имя" :required="true" state-key="firstName" v-model="storeRequests.userData.first_name"> Имя </BaseInput>
							</div>
							<div class="form__wrap">
								<BaseInput type="text" placeholder="Фамилия" :required="true" state-key="lastName" v-model="storeRequests.userData.last_name"> Фамилия </BaseInput>
							</div>

							<div class="form__wrap">
								<BasePhoneInput :required="true" state-key="phone" placeholder="+7 (900) 000 00 00" v-model="storeRequests.userData.phone_number"> Номер телефона </BasePhoneInput>
							</div>
						</div>
						<div class="form__footer">
							<button :disabled="storeField.isRecoveryPasswordBtn" :class="['send-button form__btn btn', { 'not-hover': storeRequests.loaderBtn }]">
								<span v-if="!storeRequests.loaderBtn">Изменить</span>
								<img v-else class="form-loader" width="36" height="36" src="@/assets/img/icons/loader_white.svg" alt="изображение" />
							</button>
						</div>
					</form>
				</div>

				<!-- Регистрация завершена -->
				<div class="modal-content" v-if="modalId === 'registrationEnd'">
					<h5 class="modal-title --center">Вы успешно зарегистрированы</h5>
					<p class="modal-subtitle --center" style="margin-bottom: 10px">Мы отправили вам письмо с подтверждением.</p>
					<p class="modal-subtitle --center">Проверьте папку «Входящие», а также папку «Спам». Для завершения регистрации перейдите по ссылке из письма.</p>		
				</div>

				<!-- Регистрация повтор отправили письмо -->
				<div class="modal-content" v-if="modalId === 'registrationRepeatEnd'">
					<h5 class="modal-title --center">Мы отправили вам письмо с подтверждением.</h5>
					<p class="modal-subtitle --center">Проверьте папку «Входящие», а также папку «Спам». Для завершения регистрации перейдите по ссылке из письма.</p>		
				</div>

				<!-- Акция не началась или закончилась -->
				<div class="modal-content" v-if="modalId === 'statusStock'">
					<h5 class="modal-title --center">{{ modalObj.text }}</h5>
					<!-- <button v-if="modalObj.btn" class="btn --center --small" @click="openModal('authorization')">Войти</button>		 -->
				</div>

				<!-- Опрос завершен -->
				<div class="modal-content" v-if="modalId === 'surveyEnd'">
					<h5 class="modal-title --center">Спасибо за вашу заявку!</h5>
					<p class="modal-subtitle --center" style="margin-bottom: 10px">Отслеживайте статус заявки в профиле</p>
				</div>

				<!-- Восстановление пароля send Email -->
				<div class="modal-content" v-if="modalId === 'sentNewPassword'">
					<h5 class="modal-title --center">Восстановление пароля</h5>
					<p class="modal-subtitle --center">Ссылка для восстановления пароля отправлена на вашу почту</p>
				</div>

				<!-- Восстановление пароля send Email -->
				<div class="modal-content" v-if="modalId === 'sentChangeNewPassword'">
					<h5 class="modal-title --center">Изменение пароля</h5>
					<p class="modal-subtitle --center">Ваш пароль изменен.</p>
				</div>

				<!-- Ответ на feedback -->
				<div class="modal-content" v-if="modalId === 'statusOk'">
					<h5 class="modal-title --center">Спасибо за обращение!</h5>
					<p class="modal-subtitle --center">Мы обязательно Вам ответим в течение 3 рабочих дней.</p>
					<button @click="closeModal" class="btn form__btn">Хорошо</button>
				</div>

				<!-- Акция еще не началась-->
				<div class="modal-content" v-if="modalId === 'promoNotStart'">
					<h5 class="modal-title --center --not-mb">Акция еще не началась</h5>
				</div>

				<!-- Акция закончилась -->
				<div class="modal-content" v-if="modalId === 'promoEnd'">
					<h5 class="modal-title --center --not-mb">Акция закончилась</h5>
				</div>

				<!-- photoSizeError -->
				<div class="modal-content" v-if="modalId === 'photoSizeError'">
					<h5 class="modal-title --center --not-mb">{{ modalObj.text }}</h5>
				</div>

				<!-- проверка возраста -->
				<div class="modal-content" v-if="modalId === 'ageVerification' && !props.ageVerified">
					<h5 class="modal-title --center">Вам есть 18 лет?</h5>
					<div class="buttons">
						<button class="btn --full --middle --transparent-red" @click="openModal('ageNotVerification')">нет</button>
						<button class="btn --full --middle" @click="emit('ageCheck')">да</button>
					</div>
				</div>
				<div class="modal-content" v-if="modalId === 'ageNotVerification'">
					<h5 class="modal-title --center --not-mb">
						Доступ запрещен. <br />
						Вам должно быть не менее <nobr>18 лет</nobr> 
					</h5>
				</div>
			</div>
		</Transition>
	</div>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";

.v-enter-active,
.v-leave-active {
	transition: 0.5s ease;
}

.v-enter-from {
	opacity: 0;
	transform: translateY(-10%);
}
.v-leave-to {
	opacity: 0;
	transform: translateY(-10%);
}

.modal {
	--transition-time: 0.3s;
	position: fixed;
	left: 0;
	top: 0;
	right: 0;
	bottom: 0;
	z-index: 1000;
	background-color: rgba(0, 0, 0, 0.5);
	cursor: pointer;
	overflow-y: auto;
	overflow-x: hidden;
	text-align: center;
	opacity: 0;
	visibility: hidden;
	transition:
		opacity var(--transition-time),
		visibility var(--transition-time);

		
}

.modal::before {
	content: "";
	display: inline-block;
	vertical-align: middle;
	height: 100%;
}

.modal__container {
	position: relative;
	width: 640px;
	margin: 50px auto;
	display: none;
	vertical-align: middle;
	background: var(--white);
	cursor: default;
	text-align: left;
	@media (max-width: vars.$m) {
		width: 95%;
	}
	@media (max-width: vars.$x480) {
		
	}
	&.--small {
		width: 420px;
		@media (max-width: vars.$x480) {
			width: 95%;
		}
	}
}

.modal.is-open {
	opacity: 1;
	visibility: visible;
	transition:
		opacity var(--transition-time),
		visibility var(--transition-time);
	.modal__container {
		display: inline-block;
	}
}

.modal__container.modal-open {
	display: inline-block;
}

.fade {
	opacity: 0;
	transition: opacity var(--transition-time);
}

.fade.animate-open {
	opacity: 1;
	transition: opacity var(--transition-time);
}

.fadeInUp {
	opacity: 0;
	transform: translateY(-100px);
	transition:
		opacity var(--transition-time),
		transform var(--transition-time);
}

.fadeInUp.animate-open {
	opacity: 1;
	transform: translateY(0);
	transition:
		opacity var(--transition-time),
		transform var(--transition-time);
}

.disable-scroll {
	position: relative;
	overflow: hidden;
	height: 100vh;
	position: fixed;
	left: 0;
	top: 0;
	width: 100%;
}

.modal-close {
	position: absolute;
	width: 16px;
	height: 16px;
	top: 14px;
	right: 14px;
	@media (max-width: vars.$x480) {
		top: 15px;
		right: 20px;
	}
	svg {
		stroke-width: 2px;
		stroke: var(--black);
		transition: transform var(--transition) ease-in;
	}
	@media (hover: hover) {
		&:hover {
			svg {
				transform: scale(1.1);
			}
		}
	}
}

.modal-content {
	padding: 35px 34px 44px;
	@media (max-width: vars.$x480) {
		padding: 30px 25px 30px;
	}
}
.modal-title {
	font-family: var(--font-family-md);
	font-weight: 500;
	font-size: 30px;
	line-height: 120%;	
	text-transform: uppercase;
	margin-bottom: 26px;
	color: var(--red);
	@media (max-width: vars.$x480) {
		font-size: 20px;
	}
	&.--not-mb {
		margin-bottom: 0;
	}
	&.--center {
		text-align: center;
	}
}
.modal-subtitle {
	font-weight: 400;
	font-size: 16px;
	line-height: 120%;
	&.--center {
		text-align: center;
	}
}
.buttons {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 10px;
}

.modal-content__desc {
	font-weight: 400;
	font-size: 2.4rem;
	line-height: 140%;
	color: var(--text-default-100);
	@media (max-width: vars.$x480) {
		font-size: 1.8rem;
	}
	ul {
		padding-left: 3rem;
		list-style-type: disc;
		@media (max-width: vars.$x480) {
			padding-left: 1.5rem;
		}
		li {
		}
	}
}
.modal-content__desc-title {
	font-weight: 400;
	font-size: 2.4rem;
	line-height: 140%;
	margin-bottom: 4rem;
	@media (max-width: vars.$x480) {
		font-size: 1.8rem;
		margin-bottom: 1rem;
	}
}
</style>
